import { type JSX, Fragment, useCallback, useEffect, useId, useRef, useState } from 'react';

import { FEHLERBEREICHE, FEHLERZUSTAENDE } from '@crm/kontrakte/src/fehlerablage';

import { ich } from '../api/anmeldung';
import {
  abschliessen,
  type Fehlerbereich,
  type Fehlerzeile,
  type Fehlerzustand,
  liste,
  wiederholen,
} from '../api/fehlerablage';
import { ApiFehler } from '../api/fehler';
import { alsZeitpunkt } from '../datum';
import { Hinweis, Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';

/**
 * DIE FEHLERABLAGE IM SYSTEMZUSTAND (ERR-07, ERR-10; Auftrag CRM-208).
 *
 * Die Kachel „Fehlerablage" zählt, was hier steht, und springt hierher. Die
 * Maske zeigt die Einträge mit ihrem Kontext und lässt sie erneut auslösen,
 * als erledigt schließen oder mit Grund verwerfen.
 *
 * LESEN UND HANDELN SIND ZWEI RECHTE. Die Liste sieht, wer den Systemzustand
 * sieht (`umsystem:sehen`); die Knöpfe nur, wer `umsystem:verwalten` hat —
 * dieselbe Grenze, die die API zieht. Die Arbeitsfläche reicht der Seite keine
 * Rechte herein; die Maske fragt sie beim Öffnen einmal selbst ab. Scheitert
 * das, bleiben die Knöpfe weg: Die API wiese sie ohnehin ab.
 *
 * KEIN EIGENER TAKT. `takt` zählt die Abrufe der Seite; mit jedem holt die
 * Liste im Hintergrund neu (BEN-07), sonst nur beim Öffnen, beim Filtern und
 * nach einer Handlung.
 */

/** Die Beschriftung je Bereich, wie sie in der Liste und im Filter steht. */
const BEREICHSNAME: Readonly<Record<Fehlerbereich, string>> = {
  navision: 'Navision',
  erechnung: 'E-Rechnung',
  postausgang: 'Postausgang',
  buchung: 'Buchung',
  zahlung: 'Zahlung',
  belegausgabe: 'Belegausgabe',
  /* ERR-10: ein Hintergrundlauf scheitert in Folge (0345). */
  hintergrundlauf: 'Hintergrundlauf',
};

/** Offen ist Arbeit, erledigt ist Ruhe, verworfen ist bewusst liegen gelassen. */
const ZUSTANDSMARKE: Readonly<Record<Fehlerzustand, string>> = {
  offen: 'warn',
  erledigt: 'gut',
  verworfen: 'inaktiv',
};

/** Bereiche, für die die API einen Weg zurück kennt (NOCH-OFFEN ERR-07: die übrigen mit AP-SQ-09). */
const WIEDERHOLBAR: ReadonlySet<Fehlerbereich> = new Set(['navision']);

/**
 * Ein Kontextwert als Text. Er kommt aus Fehlermeldungen der Umsysteme und
 * ist fremder Text — er geht als Textknoten in die Seite, nie als HTML.
 */
function alsText(wert: unknown): string {
  if (wert === null || wert === undefined) return '—';
  if (typeof wert === 'string') return wert;
  if (typeof wert === 'number' || typeof wert === 'boolean') return String(wert);
  return JSON.stringify(wert);
}

function Kontext({ zeile }: { readonly zeile: Fehlerzeile }): JSX.Element {
  const paare = Object.entries(zeile.kontext);
  return (
    <>
      {paare.length === 0 ? (
        <p className="hilfe">Kein Kontext erfasst.</p>
      ) : (
        /* ERR-07: der vollständige Kontext, Schlüssel und Wert als Text. */
        <dl className="angaben" aria-label={`Kontext zu ${zeile.zusammenfassung}`}>
          {paare.map(([schluessel, wert]) => (
            <Fragment key={schluessel}>
              <dt>
                <code>{schluessel}</code>
              </dt>
              <dd>{alsText(wert)}</dd>
            </Fragment>
          ))}
        </dl>
      )}
      <p className="feld__hilfe">
        Quelle <code>{zeile.quelle}</code>
      </p>
    </>
  );
}

export function Fehlerablage({ takt }: { readonly takt: number }): JSX.Element {
  const titelId = useId();
  const [bereich, setzeBereich] = useState<Fehlerbereich | ''>('');
  const [zustand, setzeZustand] = useState<Fehlerzustand | ''>('offen');
  const [eintraege, setzeEintraege] = useState<readonly Fehlerzeile[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [ladefehler, setzeLadefehler] = useState<ApiFehler | undefined>(undefined);
  const [aktionsfehler, setzeAktionsfehler] = useState<ApiFehler | undefined>(undefined);
  const [erfolg, setzeErfolg] = useState<string | undefined>(undefined);
  const [darfVerwalten, setzeDarfVerwalten] = useState(false);
  const [offen, setzeOffen] = useState<ReadonlySet<string>>(new Set());
  const [laeuft, setzeLaeuft] = useState<string | null>(null);
  const [zuVerwerfen, setzeZuVerwerfen] = useState<Fehlerzeile | null>(null);

  /* Nur die Antwort des letzten Abrufs zählt — ein langsamer alter Filter überschriebe sonst den neuen. */
  const abruf = useRef(0);

  const laden = useCallback(
    async (hintergrund = false): Promise<void> => {
      const nummer = ++abruf.current;
      try {
        const neu = await liste(
          {
            ...(bereich === '' ? {} : { bereich }),
            ...(zustand === '' ? {} : { zustand }),
          },
          hintergrund,
        );
        if (nummer !== abruf.current) return;
        setzeEintraege(neu);
        setzeLadefehler(undefined);
      } catch (f) {
        if (nummer !== abruf.current) return;
        if (f instanceof ApiFehler) setzeLadefehler(f);
        else throw f;
      } finally {
        if (nummer === abruf.current) setzeLaedt(false);
      }
    },
    [bereich, zustand],
  );

  useEffect(() => {
    void laden();
  }, [laden]);

  /*
   * Im Takt der Seite. Über einen Verweis und nicht über `laden` in den
   * Abhängigkeiten: Sonst holte jeder Filterwechsel zweimal.
   */
  const ladenVerweis = useRef(laden);
  ladenVerweis.current = laden;
  const ersterTakt = useRef(takt);
  useEffect(() => {
    // BEN-07: Der Abruf im Takt hält die Sitzung nicht wach.
    if (takt !== ersterTakt.current) void ladenVerweis.current(true);
  }, [takt]);

  useEffect(() => {
    let aktiv = true;
    ich()
      .then((auskunft) => {
        if (aktiv) setzeDarfVerwalten(auskunft.rechte.some((r) => r.objekt === 'umsystem' && r.aktion === 'verwalten'));
      })
      .catch(() => {
        /* Ohne Auskunft keine Knöpfe; die Liste bleibt lesbar. */
      });
    return () => {
      aktiv = false;
    };
  }, []);

  /** Eine Handlung, danach neu laden — auch nach einer Absage, der Stand kann sich verschoben haben. */
  const handle = async (id: string, tat: () => Promise<void>, gelungen: string): Promise<void> => {
    setzeLaeuft(id);
    setzeAktionsfehler(undefined);
    setzeErfolg(undefined);
    try {
      await tat();
      setzeErfolg(gelungen);
    } catch (f) {
      // ERR-01: Die Absage der API steht mit ihren drei Teilen da.
      if (f instanceof ApiFehler) setzeAktionsfehler(f);
      else throw f;
    } finally {
      setzeLaeuft(null);
      await laden();
    }
  };

  const umschalten = (id: string): void => {
    setzeOffen((vorher) => {
      const neu = new Set(vorher);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  };

  const spalten = darfVerwalten ? 8 : 7;

  return (
    <section className="karte" aria-labelledby={titelId}>
      <div className="karte__titel">
        <h2 id={titelId}>Fehlerablage</h2>
        <div className="knopfreihe">
          <select
            className="liste__filter"
            aria-label="Bereich"
            value={bereich}
            onChange={(e) => setzeBereich(e.target.value as Fehlerbereich | '')}
          >
            <option value="">Alle Bereiche</option>
            {FEHLERBEREICHE.map((b) => (
              <option key={b} value={b}>
                {BEREICHSNAME[b]}
              </option>
            ))}
          </select>
          <select
            className="liste__filter"
            aria-label="Zustand"
            value={zustand}
            onChange={(e) => setzeZustand(e.target.value as Fehlerzustand | '')}
          >
            <option value="">Alle Zustände</option>
            {FEHLERZUSTAENDE.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="hilfe">
        Dauerhaft gescheiterte Vorgänge aus Navision, E-Rechnung, Postausgang, Buchung, Zahlung und
        Belegausgabe sowie dauerhaft gestörte Hintergrundläufe. Erneut auslösen lässt sich bisher nur Navision; die übrigen Bereiche lösen Sie in
        ihrem Modul aus und schließen den Eintrag danach hier.
      </p>

      {ladefehler !== undefined && <Meldung fehler={ladefehler} />}
      {aktionsfehler !== undefined && <Meldung fehler={aktionsfehler} />}
      {erfolg !== undefined && <Hinweis ueberschrift={erfolg} />}

      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle">
          <caption className="nur-vorlesen">Einträge der Fehlerablage</caption>
          <thead>
            <tr>
              <th scope="col">Bereich</th>
              <th scope="col">Vorgang</th>
              <th scope="col">Zustand</th>
              <th scope="col">Zuständig</th>
              <th scope="col" className="zahl">
                Versuche
              </th>
              <th scope="col">Entstanden</th>
              <th scope="col">Zuletzt</th>
              {darfVerwalten && <th scope="col">Handlungen</th>}
            </tr>
          </thead>
          <tbody>
            {laedt && (
              <tr>
                <td colSpan={spalten}>Wird geladen …</td>
              </tr>
            )}
            {!laedt && eintraege.length === 0 && (
              <tr>
                <td colSpan={spalten}>Keine Einträge zu diesem Filter.</td>
              </tr>
            )}
            {eintraege.map((z) => {
              const aufgeklappt = offen.has(z.id);
              const sperre = laeuft !== null;
              return (
                <Fragment key={z.id}>
                  <tr>
                    <th scope="row">
                      {BEREICHSNAME[z.bereich]}
                      {/* ERR-10: die vier kritischen Fehler tragen die Marke. */}
                      {z.kritisch && (
                        <>
                          {' '}
                          <span className="marke marke--krit">kritisch</span>
                        </>
                      )}
                    </th>
                    <td>
                      {z.zusammenfassung}
                      {z.beleg !== null && (
                        <div className="hilfe">
                          {z.belegart ?? 'Beleg'} {z.beleg}
                        </div>
                      )}
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        aria-expanded={aufgeklappt}
                        onClick={() => umschalten(z.id)}
                      >
                        Kontext
                      </button>
                    </td>
                    <td>
                      <span className={`marke marke--${ZUSTANDSMARKE[z.zustand]}`}>{z.zustand}</span>
                      {z.zustand !== 'offen' && (
                        <div className="hilfe">
                          {[z.erledigtName, alsZeitpunkt(z.erledigtAm)].filter((t) => t !== null).join(', ')}
                          {z.erledigtGrund !== null && z.erledigtGrund !== '' && `: ${z.erledigtGrund}`}
                        </div>
                      )}
                    </td>
                    <td>{z.zustaendigeRolle ?? '—'}</td>
                    <td className="zahl">{z.versuche}</td>
                    <td className="zahl">{alsZeitpunkt(z.entstandenAm)}</td>
                    <td className="zahl">{alsZeitpunkt(z.zuletztAm)}</td>
                    {darfVerwalten && (
                      <td>
                        {z.zustand === 'offen' && (
                          <div className="knopfreihe">
                            {/* ERR-07: erneut auslösen, ohne Neuerfassung — nur, wo es einen Weg zurück gibt. */}
                            {WIEDERHOLBAR.has(z.bereich) && (
                              <button
                                type="button"
                                className="knopf knopf--klein"
                                disabled={sperre}
                                onClick={() =>
                                  void handle(
                                    z.id,
                                    () => wiederholen(z.id),
                                    'Erneut angestoßen. Der Eintrag bleibt offen, bis Sie ihn schließen.',
                                  )
                                }
                              >
                                Erneut auslösen
                              </button>
                            )}
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              disabled={sperre}
                              onClick={() =>
                                void handle(z.id, () => abschliessen(z.id, 'erledigt'), 'Als erledigt geschlossen.')
                              }
                            >
                              Erledigt
                            </button>
                            <button
                              type="button"
                              className="knopf knopf--klein knopf--eingriff"
                              disabled={sperre}
                              onClick={() => setzeZuVerwerfen(z)}
                            >
                              Verwerfen
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                  {aufgeklappt && (
                    <tr>
                      <td colSpan={spalten}>
                        <Kontext zeile={z} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {zuVerwerfen !== null && (
        <Rueckfrage
          frage="Eintrag verwerfen?"
          unterzeile={`${BEREICHSNAME[zuVerwerfen.bereich]}: ${zuVerwerfen.zusammenfassung}`}
          tat="Verwerfen"
          grund={{
            beschriftung: 'Grund',
            hilfe: 'Wer einen gescheiterten Vorgang verwirft, statt ihn zu beheben, entscheidet etwas.',
          }}
          beiSchliessen={() => setzeZuVerwerfen(null)}
          beiBestaetigen={async (grund) => {
            // ERR-07: Ein Verwurf braucht einen Grund; ohne ihn bleibt der Knopf gesperrt.
            await abschliessen(zuVerwerfen.id, 'verworfen', grund);
            setzeZuVerwerfen(null);
            setzeAktionsfehler(undefined);
            setzeErfolg('Verworfen.');
            await laden();
          }}
        >
          <p>
            Der Vorgang wird nicht mehr angestoßen und fällt aus der Liste der offenen Einträge. Der Grund
            bleibt am Eintrag stehen.
          </p>
        </Rueckfrage>
      )}
    </section>
  );
}
